import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  createStyles,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Stepper,
  Tabs,
  Text,
} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS, MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import FolderSelector from 'file-browser/FolderSelector';
import type {Tag} from 'folder-items/FileConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {isAaSequenceNode} from 'graphql-client/aaSequenceTypenames';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY_WRAPPER} from 'model-hub/ModelHubQueries';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import type {ItemListingSchemas} from 'nav/sidepanel/listing/ItemListingUtil';
import type {ItemListingSchemasByType, SchemasFilterFunction} from 'nav/sidepanel/listing/ItemListingUtil';
import notifications from 'notifications';
import type {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {
  type Filter,
  FilterKey,
  keyToEmptyFilter,
  type ListableItem,
  type ListableItemExtras,
  Operator,
} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import type {ListBioentities, ListBioentitiesVariables} from 'worklists/__generated/ListBioentities';
import {LIST_BIOENTITIES_QUERY} from 'worklists/BioentityWorklistItems';
import {WorklistType} from 'worklists/WorklistConstants';
import {fetchWorklist} from 'worklists/WorklistDAO';
import WorklistSearchInput from 'worklists/WorklistSearchInput';

import type {BulkPredictionItem} from '../input-forms/BulkPredictionUtils';
import {
  getAvailableTypesForModel,
  isEntityTypeCompatible,
  submitBulkPrediction,
} from '../input-forms/BulkPredictionUtils';
import SelectedEntitiesArea from '../input-forms/SelectedEntitiesArea';
import {MAX_BULK_PREDICTION_ITEMS} from '../ScientificModelConstants';
import {
  getMaxBulkPredictionItems,
  getModelIconUrl,
  getUseMSAForSubmission,
  isMSAConfigurable,
  type ScientificModelNode,
  MODEL_CATEGORY,
} from '../ScientificModelMetadata';
import {buildCustomEntityPredictionConfigs, type CustomEntityWithTags} from './CustomEntityPredictionUtils';
import CustomEntitySelectionSetup, {type CustomEntityFieldMapping} from './CustomEntitySelectionSetup';

enum SearchTabValue {
  SEARCH = 'search',
  WORKLIST = 'worklist',
}

enum SelectionSource {
  CUSTOM_ENTITIES = 'custom-entities',
  DIRECT_ENTITIES = 'direct-entities',
}

enum DataFirstStep {
  SELECT_DATA = 0,
  SELECT_MODELS = 1,
}

const CATEGORY_LABELS: Record<string, string> = {
  structure_prediction: 'Structure Prediction',
  generative: 'Generative',
  property_prediction: 'Property Prediction',
};

type TypesFilterTagSchema = TypesFilterSchemasQuery['tagSchemas'][number];
type ItemListingTagSchema = ItemListingSchemas['tagSchemas'][number];

function getCustomEntitySchemaFilter(schemaId: string): SchemasFilterFunction {
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType => {
    const filteredSchemas: ItemListingSchemasByType = {};
    for (const type of Object.keys(schemasByAvailableType)) {
      const tagSchemas = schemasByAvailableType[type] as Array<ItemListingTagSchema | TypesFilterTagSchema>;
      filteredSchemas[type] = tagSchemas.filter((schema) => schema.id === schemaId);
    }
    return filteredSchemas;
  };
}

interface DataFirstTabProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  onNavigateToRuns: () => void;
}

export default function DataFirstTab({availableModels, onNavigateToRuns}: DataFirstTabProps): JSX.Element {
  const {classes} = useStyles();
  const {classes: tabClasses} = useSearchTabStyles();

  const [activeStep, setActiveStep] = useState<DataFirstStep>(DataFirstStep.SELECT_DATA);
  const [items, setItems] = useState<Array<BulkPredictionItem>>([]);
  const [selectionSource, setSelectionSource] = useState<SelectionSource>(SelectionSource.DIRECT_ENTITIES);
  const [customEntityMapping, setCustomEntityMapping] = useState<CustomEntityFieldMapping | null>(null);
  const [searchTab, setSearchTab] = useState<string | null>(SearchTabValue.SEARCH);
  const [selectedModels, setSelectedModels] = useState<Set<ScientificModelNode>>(new Set());
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    () => FolderDefaults.get(FolderDefaultsKey.BULK_MODEL_PREDICTION) ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedCount, setSubmittedCount] = useState(0);
  const [msaEnabled, setMsaEnabled] = useState(true);

  const structurePredictionModels = useMemo(
    () => availableModels.filter((model) => model.category === MODEL_CATEGORY.STRUCTURE_PREDICTION),
    [availableModels]
  );
  const customEntityInputTypes = useMemo(
    () => [...new Set(availableModels.flatMap((model) => model.inputTypes))],
    [availableModels]
  );

  const compatibleModels = useMemo(() => {
    let filtered =
      selectionSource === SelectionSource.CUSTOM_ENTITIES
        ? availableModels.filter(
            (model) => customEntityMapping != null && model.inputTypes.includes(customEntityMapping.inputType)
          )
        : structurePredictionModels;
    if (items.length > 0) {
      if (selectionSource === SelectionSource.DIRECT_ENTITIES) {
        const selectedEntityTypes = new Set(items.map((item) => item.entityType));
        filtered = filtered.filter((m) =>
          [...selectedEntityTypes].every((et) => isEntityTypeCompatible(et, m.inputTypes))
        );
      }
      filtered = filtered.filter((m) => items.length <= getMaxBulkPredictionItems(m));
    }
    return filtered;
  }, [availableModels, customEntityMapping, structurePredictionModels, items, selectionSource]);
  const showMsaOption = useMemo(
    () => compatibleModels.some((model) => isMSAConfigurable(model)),
    [compatibleModels]
  );

  const allInputTypes = useMemo(() => {
    const allTypes = new Set<string>();
    for (const model of structurePredictionModels) {
      for (const t of model.inputTypes) {
        allTypes.add(t);
      }
    }
    return [...allTypes];
  }, [structurePredictionModels]);

  const availableTypes = useMemo(() => getAvailableTypesForModel(allInputTypes), [allInputTypes]);
  const customEntityFilters = useMemo<Array<Filter>>(
    () =>
      customEntityMapping == null
        ? []
        : [
            {
              ...keyToEmptyFilter(FilterKey.SCHEMA),
              locked: true,
              operator: Operator.IS_ONE_OF,
              value: [customEntityMapping.schemaId],
            },
          ],
    [customEntityMapping]
  );
  const customEntitySchemaFilter = useMemo(
    () =>
      customEntityMapping == null ? undefined : getCustomEntitySchemaFilter(customEntityMapping.schemaId),
    [customEntityMapping]
  );

  const isAtCapacity = items.length >= MAX_BULK_PREDICTION_ITEMS;
  const selectedBioentityIds = useMemo(() => items.map((item) => item.id), [items]);

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.DATA_FIRST_TAB_VIEWED);
  }, []);

  useEffect(() => {
    if (selectionSource === SelectionSource.CUSTOM_ENTITIES && customEntityMapping != null) {
      clearBioentitySearchViewSearchParams(
        [ModelTypeConstants.BASIC_FOLDER_ITEM],
        Section.MODEL_HUB_STRUCTURE_PREDICTION,
        customEntityFilters
      );
    } else if (selectionSource === SelectionSource.DIRECT_ENTITIES) {
      clearBioentitySearchViewSearchParams(availableTypes, Section.MODEL_HUB_STRUCTURE_PREDICTION);
    }
  }, [availableTypes, customEntityFilters, customEntityMapping, selectionSource]);

  const handleBioentitySelectionChange = useCallback(
    (newSelectedIds: Array<string>) => {
      if (newSelectedIds.length > MAX_BULK_PREDICTION_ITEMS) {
        notifications.error(
          `Maximum of ${MAX_BULK_PREDICTION_ITEMS} entities selected. Deselect an entity to swap in this one.`
        );
        return;
      }
      setItems(
        newSelectedIds.map((id) => ({
          id,
          entityType:
            selectionSource === SelectionSource.CUSTOM_ENTITIES
              ? ModelTypeConstants.BASIC_FOLDER_ITEM
              : APIUtil.apiIdToType(id),
        }))
      );
    },
    [selectionSource]
  );

  const isItemInvalid = useCallback((item: ListableItem) => {
    if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROTEIN) {
      return (
        (item.permissions as ListableItemExtras['permissions'] & {structurePredictionEditable?: boolean})
          .structurePredictionEditable !== true
      );
    }
    return false;
  }, []);

  const getInvalidItemTooltipText = useCallback(
    (item: ListableItem): string | undefined => {
      if (isItemInvalid(item)) {
        return 'You do not have permission to run structure prediction on this AA sequence';
      }
      return undefined;
    },
    [isItemInvalid]
  );

  const handleRemoveItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleClearAll = useCallback(() => {
    setItems([]);
  }, []);

  const handleSelectionSourceChange = useCallback((source: string) => {
    setSelectionSource(source as SelectionSource);
    setCustomEntityMapping(null);
    setItems([]);
    setSelectedModels(new Set());
  }, []);

  const handleCustomEntityMappingContinue = useCallback((mapping: CustomEntityFieldMapping) => {
    setCustomEntityMapping(mapping);
    setItems([]);
  }, []);

  const handleEditCustomEntityMapping = useCallback(() => {
    setCustomEntityMapping(null);
    setItems([]);
  }, []);

  const handleWorklistSelect = async (worklist: {id: string} | null): Promise<void> => {
    if (worklist == null) {
      return;
    }

    try {
      const fullWorklist = await fetchWorklist(worklist.id);
      const allItemIds = fullWorklist.itemIds;
      const existingIds = new Set(items.map((item) => item.id));
      const candidateItems: Array<BulkPredictionItem> = [];
      let skippedDuplicates = 0;
      let skippedIncompatible = 0;

      for (const itemId of allItemIds) {
        const entityType = APIUtil.apiIdToType(itemId);
        if (!isEntityTypeCompatible(entityType, allInputTypes)) {
          skippedIncompatible++;
        } else if (existingIds.has(itemId)) {
          skippedDuplicates++;
        } else {
          candidateItems.push({id: itemId, entityType});
        }
      }

      const proteinIds = candidateItems.flatMap((item) =>
        item.entityType === ModelTypeConstants.PROTEIN ? [item.id] : []
      );
      const editableProteinIds = new Set<string>();
      let skippedInvalid = 0;

      if (proteinIds.length > 0) {
        const proteinResult = await doQuerySafe(PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY_WRAPPER, {
          variables: {ids: proteinIds},
        });
        proteinResult.aaSequences.forEach((protein) => {
          if (isAaSequenceNode(protein) && protein.permissions.structurePredictionEditable === true) {
            editableProteinIds.add(protein.id);
          }
        });
      }

      const compatible: Array<BulkPredictionItem> = [];
      for (const item of candidateItems) {
        if (item.entityType === ModelTypeConstants.PROTEIN && !editableProteinIds.has(item.id)) {
          skippedInvalid++;
        } else {
          compatible.push(item);
        }
      }

      const remainingCapacity = MAX_BULK_PREDICTION_ITEMS - items.length;
      if (compatible.length > remainingCapacity) {
        notifications.error(
          `Cannot add worklist — ${compatible.length} eligible items would exceed the limit of ${MAX_BULK_PREDICTION_ITEMS}. Remove some items first.`
        );
        return;
      }

      setItems((prev) => [...prev, ...compatible]);

      if (skippedIncompatible > 0 || skippedInvalid > 0 || skippedDuplicates > 0) {
        const parts: Array<string> = [];
        if (skippedIncompatible > 0) {
          parts.push(
            `${skippedIncompatible} incompatible ${pluralize(skippedIncompatible, 'item', 'items')} skipped`
          );
        }
        if (skippedInvalid > 0) {
          parts.push(`${skippedInvalid} invalid ${pluralize(skippedInvalid, 'item', 'items')} skipped`);
        }
        if (skippedDuplicates > 0) {
          parts.push(
            `${skippedDuplicates} duplicate ${pluralize(skippedDuplicates, 'item', 'items')} skipped`
          );
        }
        notifications.info(`${compatible.length} of ${allItemIds.length} items added (${parts.join(', ')})`);
      }
    } catch (e) {
      notifications.error('Failed to load worklist items.');
      if (!(e instanceof Error)) {
        throw e;
      }
    }
  };

  const handleToggleModel = useCallback((model: ScientificModelNode) => {
    setSelectedModels((prev) => {
      const next = new Set(prev);
      if (next.has(model)) {
        next.delete(model);
      } else {
        next.add(model);
      }
      return next;
    });
  }, []);

  const handleSelectAllModels = useCallback(() => {
    setSelectedModels(new Set(compatibleModels));
  }, [compatibleModels]);

  const handleDeselectAllModels = useCallback(() => {
    setSelectedModels(new Set());
  }, []);

  const handleGoToModelSelection = useCallback(() => {
    setSelectedModels((prev) => {
      const validModels = new Set(compatibleModels);
      const next = new Set<ScientificModelNode>();
      for (const m of prev) {
        if (validModels.has(m)) {
          next.add(m);
        }
      }
      return next;
    });
    setActiveStep(DataFirstStep.SELECT_MODELS);
  }, [compatibleModels]);

  const handleBackToData = useCallback(() => {
    setActiveStep(DataFirstStep.SELECT_DATA);
    setSelectedModels(new Set());
    setError(null);
    setSubmittedCount(0);
    setIsSubmitting(false);
    setMsaEnabled(true);
  }, []);

  const handleSubmit = async (): Promise<void> => {
    if (selectedFolderId == null || items.length === 0 || selectedModels.size === 0) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSubmittedCount(0);

    let customEntities: Array<CustomEntityWithTags> | undefined;
    if (selectionSource === SelectionSource.CUSTOM_ENTITIES) {
      if (customEntityMapping == null) {
        setIsSubmitting(false);
        return;
      }
      try {
        const result = await doQuerySafe<ListBioentities, ListBioentitiesVariables>(
          {query: LIST_BIOENTITIES_QUERY},
          {variables: {bioentityIds: items.map((item) => item.id)}}
        );
        customEntities = result.bioentities.map((entity): CustomEntityWithTags => {
          if (entity.__typename !== 'BasicFolderItemNode') {
            throw new Error('One or more selected custom entities are no longer accessible.');
          }
          return {
            id: entity.id,
            name: entity.name,
            tags: entity.tagsJson as Array<Tag>,
          };
        });
      } catch (e) {
        if (!(e instanceof Error)) {
          throw e;
        }
        setError(e.message);
        setIsSubmitting(false);
        return;
      }
    }

    const modelsToRun = [...selectedModels];
    let succeeded = 0;
    const failures: Array<string> = [];

    for (const model of modelsToRun) {
      try {
        const batchName = `${model.displayName} — Data-first batch`;
        const useMsa = getUseMSAForSubmission(model, msaEnabled);
        const customPredictionConfigs =
          customEntities == null || customEntityMapping == null
            ? undefined
            : buildCustomEntityPredictionConfigs(customEntities, customEntityMapping, model);
        await submitBulkPrediction({
          folderId: selectedFolderId,
          model,
          ...(customPredictionConfigs == null ? {items} : {predictionConfigs: customPredictionConfigs}),
          modelParams: {useMsa},
          source: MODEL_RUN_SOURCE.MODEL_HUB_UI,
          name: batchName,
        });
        succeeded++;
        setSubmittedCount(succeeded);
      } catch (e) {
        if (e instanceof Error) {
          failures.push(model.displayName);
        } else {
          throw e;
        }
      }
    }

    setIsSubmitting(false);

    if (failures.length === 0) {
      FolderDefaults.set(FolderDefaultsKey.BULK_MODEL_PREDICTION, selectedFolderId);
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.DATA_FIRST_BATCH_SUBMITTED, {
        entityCount: items.length,
        modelCount: modelsToRun.length,
      });
      notifications.success(
        `Created ${succeeded} prediction ${pluralize(succeeded, 'batch', 'batches')} with ${items.length} ${pluralize(items.length, 'entity', 'entities')} each.`
      );
      onNavigateToRuns();
    } else {
      setError(`${succeeded} of ${modelsToRun.length} batches created. Failed: ${failures.join(', ')}`);
    }
  };

  const renderDirectEntitySelection = (): JSX.Element => (
    <>
      <Alert color="blue" mb="md">
        Select up to {MAX_BULK_PREDICTION_ITEMS} entities, then choose which models to run them against.
      </Alert>

      <SelectedEntitiesArea
        items={items}
        maxBulkPredictionItems={MAX_BULK_PREDICTION_ITEMS}
        onRemoveItem={handleRemoveItem}
        onClearAll={handleClearAll}
      />

      <div className={classes.searchArea}>
        <Tabs
          value={searchTab}
          onTabChange={(value) => {
            if (value != null) {
              setSearchTab(value);
            }
          }}
          variant="pills"
          classNames={tabClasses}
        >
          <Tabs.List>
            <Tabs.Tab value={SearchTabValue.SEARCH} icon={<SearchTabIcon />}>
              Search
            </Tabs.Tab>
            <Tabs.Tab value={SearchTabValue.WORKLIST} icon={<WorklistTabIcon />}>
              Add from worklist
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={SearchTabValue.SEARCH}>
            <div className={classes.searchViewContainer}>
              <BioentitySearchView
                navSection={Section.MODEL_HUB_STRUCTURE_PREDICTION}
                availableTypes={availableTypes}
                selectedBioentityIds={selectedBioentityIds}
                setSelectedBioentityIds={handleBioentitySelectionChange}
                rowSelectionMode={RowSelectionMode.MULTIPLE}
                limitSelectedEntitiesParams={
                  isAtCapacity
                    ? {
                        maxNumEntitiesToAdd: MAX_BULK_PREDICTION_ITEMS,
                        selectedEntityName: 'entities',
                      }
                    : undefined
                }
                isItemInvalid={isItemInvalid}
                getInvalidItemTooltipText={getInvalidItemTooltipText}
                showInvalid={true}
              />
            </div>
          </Tabs.Panel>

          <Tabs.Panel value={SearchTabValue.WORKLIST}>
            <div className={classes.worklistContent}>
              <div className={classes.worklistSearchContainer}>
                <WorklistSearchInput
                  autoFocus={false}
                  disabled={isAtCapacity}
                  onChange={handleWorklistSelect}
                  worklistTypes={[WorklistType.BIOENTITY]}
                />
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>
      </div>
    </>
  );

  const renderCustomEntitySelection = (): JSX.Element => {
    if (customEntityMapping == null) {
      return (
        <div className={classes.scrollableContent}>
          <CustomEntitySelectionSetup
            inputTypes={customEntityInputTypes}
            onContinue={handleCustomEntityMappingContinue}
          />
        </div>
      );
    }

    return (
      <>
        <Paper withBorder={true} radius="md" p="md" mb="md">
          <Group position="apart">
            <Stack spacing={4}>
              <Group spacing="xs">
                <Text weight={600}>{customEntityMapping.schemaName}</Text>
                <Badge variant="light">Custom entity</Badge>
                <Badge color="indigo" variant="light">
                  {customEntityMapping.inputTypeDisplayName}
                </Badge>
              </Group>
              <Group spacing="xs">
                {customEntityMapping.fieldMappings.map((fieldMapping, index) => (
                  <React.Fragment key={fieldMapping.inputFieldKey}>
                    {index > 0 && (
                      <Text size="sm" color="dimmed">
                        ·
                      </Text>
                    )}
                    <Text size="sm" color="dimmed">
                      {fieldMapping.inputFieldDisplayName} ← {fieldMapping.schemaFieldName}
                    </Text>
                  </React.Fragment>
                ))}
              </Group>
            </Stack>
            <Button variant="ghost" compact={true} onClick={handleEditCustomEntityMapping}>
              Edit mapping
            </Button>
          </Group>
        </Paper>

        <SelectedEntitiesArea
          items={items}
          maxBulkPredictionItems={MAX_BULK_PREDICTION_ITEMS}
          onRemoveItem={handleRemoveItem}
          onClearAll={handleClearAll}
        />

        <div className={classes.searchArea}>
          <Text weight={600} mb="sm">
            Browse {customEntityMapping.schemaName}
          </Text>
          <div className={classes.searchViewContainer}>
            <BioentitySearchView
              navSection={Section.MODEL_HUB_STRUCTURE_PREDICTION}
              availableTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
              filterSchemas={customEntitySchemaFilter}
              initialFilters={customEntityFilters}
              selectedBioentityIds={selectedBioentityIds}
              setSelectedBioentityIds={handleBioentitySelectionChange}
              rowSelectionMode={RowSelectionMode.MULTIPLE}
              limitSelectedEntitiesParams={
                isAtCapacity
                  ? {
                      maxNumEntitiesToAdd: MAX_BULK_PREDICTION_ITEMS,
                      selectedEntityName: 'custom entities',
                    }
                  : undefined
              }
            />
          </div>
        </div>
      </>
    );
  };

  const renderDataSelectionStep = (): JSX.Element => {
    const isCustomEntitySetup =
      selectionSource === SelectionSource.CUSTOM_ENTITIES && customEntityMapping == null;
    return (
      <div className={classes.stepContent}>
        <Paper withBorder={true} radius="md" p="sm" mb="md">
          <Group position="apart" noWrap={true}>
            <Stack spacing={2}>
              <Text weight={600}>Select data from</Text>
              <Text size="sm" color="dimmed">
                Choose direct model inputs or map fields from a custom entity type.
              </Text>
            </Stack>
            <SegmentedControl
              aria-label="Data source"
              data={[
                {label: 'Sequences & molecules', value: SelectionSource.DIRECT_ENTITIES},
                {label: 'Custom entities', value: SelectionSource.CUSTOM_ENTITIES},
              ]}
              onChange={handleSelectionSourceChange}
              value={selectionSource}
            />
          </Group>
        </Paper>

        {selectionSource === SelectionSource.DIRECT_ENTITIES
          ? renderDirectEntitySelection()
          : renderCustomEntitySelection()}

        {!isCustomEntitySetup && (
          <div className={classes.footer}>
            <div />
            <Tooltip text="Please add at least one entity" isDisabled={items.length > 0}>
              <Button size="md" disabled={items.length === 0} onClick={handleGoToModelSelection}>
                Next: Choose Models ({items.length} {pluralize(items.length, 'entity', 'entities')} selected)
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderModelSelectionStep = (): JSX.Element => (
    <div className={classes.stepContent}>
      {error && <InlineBanner text={error} bannerType={InlineBannerType.ERROR} />}

      <Stack spacing="md" className={classes.scrollableContent}>
        <Group position="apart">
          <Text weight={600}>
            Select models to run on {items.length} {pluralize(items.length, 'entity', 'entities')}
          </Text>
          <Group spacing="xs">
            <Button variant="ghost" size="xs" compact={true} onClick={handleSelectAllModels}>
              Select all
            </Button>
            <Button variant="ghost" size="xs" compact={true} onClick={handleDeselectAllModels}>
              Deselect all
            </Button>
          </Group>
        </Group>

        <div className={classes.section}>
          <Text size="sm" weight={600} mb={8}>
            Output Folder{' '}
            <Text component="span" color="red">
              *
            </Text>
          </Text>
          <FolderSelector
            value={selectedFolderId}
            onChange={(e: {target: {value: string | null}}) => {
              setSelectedFolderId(e.target.value);
            }}
            allowNone={false}
            enableRedesign={false}
            error=""
            level="append"
            noFolderText="Choose folder"
            readOnly={false}
            requireSubfolderCreatableAuth={false}
            showInventory={false}
            showNotebook={true}
            showRegistries={false}
            unsetOnFetchFail={false}
            checkExistingItemForDataModsMovement={false}
          />
        </div>

        {showMsaOption && (
          <Checkbox
            checked={msaEnabled}
            label="Use MSAs"
            onChange={(event) => {
              setMsaEnabled(event.currentTarget.checked);
            }}
          />
        )}

        <Divider />

        {compatibleModels.length === 0 ? (
          <Text color="dimmed">No compatible models found for the selected entities.</Text>
        ) : (
          <Stack spacing="sm">
            {compatibleModels.map((model) => (
              <Paper
                key={model.externalReference}
                withBorder={true}
                radius="sm"
                p="sm"
                className={selectedModels.has(model) ? classes.modelCardSelected : classes.modelCard}
                role="checkbox"
                tabIndex={0}
                aria-checked={selectedModels.has(model)}
                aria-label={`Select ${model.displayName}`}
                onClick={() => {
                  handleToggleModel(model);
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    handleToggleModel(model);
                  }
                }}
              >
                <Group noWrap={true} spacing="md">
                  <Checkbox
                    checked={selectedModels.has(model)}
                    tabIndex={-1}
                    onChange={() => {
                      handleToggleModel(model);
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                    }}
                    aria-hidden={true}
                  />
                  <Box className={classes.modelIconContainer}>
                    <img src={getModelIconUrl(model)} alt={model.displayName} className={classes.modelIcon} />
                  </Box>
                  <Stack spacing={2}>
                    <Group spacing="xs">
                      <Text weight={600} size="sm">
                        {model.displayName}
                      </Text>
                      <Badge size="xs" variant="light" color="indigo">
                        {CATEGORY_LABELS[model.category] ?? model.category}
                      </Badge>
                      {model.capabilities.isPreview && (
                        <Badge size="xs" variant="light" color="yellow">
                          Preview
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" color="dimmed" lineClamp={1}>
                      {model.description}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>

      <div className={classes.footer}>
        <Button variant="ghost" size="md" onClick={handleBackToData}>
          Back
        </Button>
        <Tooltip
          text={
            selectedModels.size === 0
              ? 'Select at least one model'
              : selectedFolderId == null
                ? 'Select an output folder'
                : null
          }
          isDisabled={selectedModels.size > 0 && selectedFolderId != null}
        >
          <Button
            size="md"
            disabled={selectedModels.size === 0 || selectedFolderId == null || isSubmitting}
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {isSubmitting
              ? `Submitting (${submittedCount}/${selectedModels.size})...`
              : `Run ${selectedModels.size} ${pluralize(selectedModels.size, 'model', 'models')}`}
          </Button>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <div className={classes.container}>
      <Stepper
        active={activeStep}
        onStepClick={(step) => {
          if (step === DataFirstStep.SELECT_DATA) {
            handleBackToData();
          } else if (step === DataFirstStep.SELECT_MODELS && items.length > 0) {
            handleGoToModelSelection();
          }
        }}
        size="sm"
        mb="lg"
      >
        <Stepper.Step
          label="Select Data"
          description={items.length > 0 ? `${items.length} entities` : undefined}
        />
        <Stepper.Step
          label="Choose Models"
          description={selectedModels.size > 0 ? `${selectedModels.size} selected` : undefined}
        />
      </Stepper>

      {activeStep === DataFirstStep.SELECT_DATA && renderDataSelectionStep()}
      {activeStep === DataFirstStep.SELECT_MODELS && renderModelSelectionStep()}
    </div>
  );
}

function SearchTabIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function WorklistTabIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    overflow: 'hidden',
  },
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  section: {
    marginBottom: theme.spacing.md,
  },
  searchArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  searchViewContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  worklistContent: {
    padding: `${theme.spacing.md} 0`,
  },
  worklistSearchContainer: {
    maxWidth: 350,
  },
  footer: {
    borderTop: `1px solid ${theme.colors.gray[3]}`,
    paddingTop: theme.spacing.lg,
    marginTop: theme.spacing.lg,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modelCard: {
    cursor: 'pointer',
    transition: 'border-color 150ms ease',
    '&:hover': {
      borderColor: theme.colors.blue[3],
    },
  },
  modelCardSelected: {
    cursor: 'pointer',
    borderColor: theme.colors.blue[5],
    backgroundColor: theme.colors.blue[0],
  },
  modelIconContainer: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
  },
  modelIcon: {
    maxWidth: '80%',
    maxHeight: '80%',
    objectFit: 'contain' as const,
  },
}));

const useSearchTabStyles = createStyles((theme) => ({
  root: {display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'},
  tabsList: {gap: 0, borderBottom: 'none', marginBottom: theme.spacing.md},
  tab: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 500,
    padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    color: theme.colors.gray[7],
    '&:first-of-type': {
      borderTopLeftRadius: theme.radius.lg,
      borderBottomLeftRadius: theme.radius.lg,
    },
    '&:last-of-type': {
      borderTopRightRadius: theme.radius.lg,
      borderBottomRightRadius: theme.radius.lg,
      borderLeft: 'none',
    },
    '&[data-active]': {
      backgroundColor: theme.colors.blue[0],
      color: theme.colors.blue[7],
      fontWeight: 600,
      borderColor: theme.colors.blue[4],
    },
    '&:not([data-active])': {
      backgroundColor: theme.white,
    },
  },
  tabIcon: {
    marginRight: theme.spacing.sm,
  },
  panel: {flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden'},
}));
